/**
 * Frontend Sentry initialization — gated by the observability module + DSN.
 *
 * `initObservability()` is the single entrypoint the app shell calls at
 * startup. It is a no-op unless BOTH the observability module flag is on AND a
 * DSN is configured (`shouldInitSentry()`), so disabled builds pay no cost.
 *
 * The `@sentry/react` import is dynamic so the package is only loaded when
 * Sentry is actually enabled — and the build does not break if the optional
 * dependency is not installed yet (the import failure is caught and logged).
 *
 * PII scrubbing default: `sendDefaultPii: false` — no IP/cookies are attached
 * automatically. Release/environment tagging mirrors the backend.
 *
 * Request-ID correlation: the current client request ID (set by the API
 * middleware seam) is attached as a Sentry tag via `beforeSend`, so a
 * frontend Sentry event can be cross-referenced with backend structured logs
 * that carry the same `X-Request-ID`.
 */

import {
  SENTRY_DSN,
  SENTRY_ENVIRONMENT,
  SENTRY_RELEASE,
  SENTRY_TRACES_SAMPLE_RATE,
  shouldInitSentry,
} from './config';

// ---------------------------------------------------------------------------
// Client request-ID seam
// ---------------------------------------------------------------------------
// The API middleware sets a per-request ID on outgoing fetches (X-Request-ID
// header). We expose a setter here so the middleware can register the current
// request ID without creating a cross-layer import. The stored value is
// attached to Sentry events via beforeSend for FE↔BE log correlation.

let _currentRequestId: string | null = null;

/**
 * Register the current client-side request ID.
 * Called by the API middleware after each outgoing request assigns a new ID.
 * Calling with `null` clears the stored value.
 */
export function setCurrentRequestId(id: string | null): void {
  _currentRequestId = id;
}

/**
 * Return the most recently registered client request ID, or `null`.
 * Used by Sentry's `beforeSend` hook to tag events.
 */
export function getCurrentRequestId(): string | null {
  return _currentRequestId;
}

// ---------------------------------------------------------------------------
// Sentry init
// ---------------------------------------------------------------------------

let initialized = false;

/**
 * Initialize browser error/performance monitoring if gating conditions hold.
 *
 * @returns A promise resolving to `true` if Sentry was initialized, else `false`.
 */
export async function initObservability(): Promise<boolean> {
  if (initialized) return true;
  if (!shouldInitSentry()) return false;

  try {
    // Dynamic import: only loaded when enabled; optional dependency.
    const Sentry = await import(/* @vite-ignore */ '@sentry/react');

    Sentry.init({
      dsn: SENTRY_DSN,
      environment: SENTRY_ENVIRONMENT,
      release: SENTRY_RELEASE || undefined,
      tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
      // PII scrubbing default — do not attach IP/cookies automatically.
      sendDefaultPii: false,
      // Attach the client request ID for FE↔BE log correlation.
      beforeSend(event) {
        const requestId = getCurrentRequestId();
        if (requestId) {
          event.tags = { ...event.tags, request_id: requestId };
        }
        return event;
      },
    });

    initialized = true;
    return true;
  } catch (err) {
    // The optional @sentry/react dependency may not be installed yet.
    // Never let observability wiring break app startup.
    // eslint-disable-next-line no-console
    console.warn('Observability is enabled but Sentry could not be initialized.', err);
    return false;
  }
}

/**
 * Capture an exception in Sentry if it has been initialized.
 * A no-op (and never imports @sentry/react) when Sentry is not initialized.
 *
 * Used by AppErrorBoundary to forward caught render errors.
 *
 * @param error   The error to capture.
 * @param context Optional extra context (e.g. componentStack).
 */
export async function captureObservabilityException(
  error: Error,
  context?: Record<string, string>,
): Promise<void> {
  if (!initialized) return;

  try {
    const Sentry = await import(/* @vite-ignore */ '@sentry/react');
    Sentry.captureException(error, context ? { extra: context } : undefined);
  } catch {
    // Never let capture failures propagate.
  }
}

/** Test/seam helper: report whether Sentry has been initialized. */
export function isObservabilityInitialized(): boolean {
  return initialized;
}

/**
 * Reset initialization state.
 * FOR TESTS ONLY — allows re-initialization in unit test suites.
 */
export function _resetObservabilityForTest(): void {
  initialized = false;
  _currentRequestId = null;
}
